import {gql} from '@apollo/client';
import {Box, createStyles} from '@mantine/core';
import {DocumentNode} from 'graphql';
import React from 'react';

import {BaseSeedDataConfig, SeedDataConfigType} from 'analysis-tool/create-analysis-modal/SeedDataTypes';
import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs, getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import HelpButton from 'help/HelpButton';
import OOTBChartingCarousel from 'studies/typed/shared/OOTBChartingCarousel';
import {
  INACCESSIBLE_STUDY_ERROR_TEXT,
  SELECTED_UNIT_OPERATIONS_LABEL,
} from 'studies/typed/shared/ProcedureRunAnalysisConstants';
import {getTypedStudyAdapter} from 'studies/typed/TypedStudyAdapters';
import {StudyInaccessibleContentBanner} from 'studies/typed/utils/ui/StudyQueryBanners';
import {getProcedureRunUIStringForStudyType} from 'studies/TypedStudyUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {CenteredSpinner} from 'ui-library/Spinner';

import {
  ProcedureRunsAnalysisQuery,
  ProcedureRunsAnalysisQueryVariables,
} from './__generated/ProcedureRunsAnalysisQuery';

export const INACCESSIBLE_RUNS_ERROR_TEXT =
  'Some runs are no longer accessible. Try refreshing the page if this is unexpected.';

export const OOTB_STUDY_DESCRIPTION = "Preview what we'll build to help you learn about your study.";

export function getInaccessibleRunsWarningText(studyType: StudyType): string {
  const runsString = getProcedureRunUIStringForStudyType(studyType, {plural: true});
  return `You do not have access to all ${runsString} in this study. This analysis will only include the ${runsString} that you have access to.`;
}

export function getUnitOperationCountFromProcedureRuns<
  T extends {methodExecutionInstances: ReadonlyArray<unknown>},
>(procedureRuns: ReadonlyArray<T>): number {
  return procedureRuns.reduce((accumulator, run) => accumulator + run.methodExecutionInstances.length, 0);
}

export type ProcedureRunSeedDataConfig = BaseSeedDataConfig<
  SeedDataConfigType.PROCEDURE_RUN,
  {
    procedureRunIds: Array<string>;
  }
>;

export function validateProcedureRunSeedDataConfigValid(config: ProcedureRunSeedDataConfig): boolean {
  return config.data.procedureRunIds.length > 0;
}

export function ProcedureRunSeedDataConfigEditor(props: {
  value: ProcedureRunSeedDataConfig;
  onChange: (value: ProcedureRunSeedDataConfig) => void;
}): JSX.Element {
  const procedureRunQueryResult = useSkippableBenchlingQuery<
    ProcedureRunsAnalysisQuery,
    ProcedureRunsAnalysisQueryVariables
  >(PROCEDURE_RUNS_ANALYSIS_QUERY, {
    skip: props.value.data.procedureRunIds.length === 0,
    variables: {ids: props.value.data.procedureRunIds},
    renderCachedDataWhenRefetching: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  // Empty IDs skip the query (SKIPPED); we still assert so invalid renders fail fast without firing PMEI with [].
  assert(props.value.data.procedureRunIds.length > 0, 'Expected at least one procedure run to be selected');
  if (
    procedureRunQueryResult.type === QueryResultType.PENDING ||
    procedureRunQueryResult.type === QueryResultType.ERROR ||
    procedureRunQueryResult.type === QueryResultType.SKIPPED
  ) {
    return <CenteredSpinner />;
  }

  const accessibleProcedureRuns = accessibleObjs(procedureRunQueryResult.data.procedureRuns);
  if (accessibleProcedureRuns.length !== procedureRunQueryResult.data.procedureRuns.length) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={INACCESSIBLE_RUNS_ERROR_TEXT} />;
  }

  const studyIds = [
    ...new Set(
      accessibleProcedureRuns.map((procedureRun) => getAccessibleOrInaccessibleId(procedureRun.study))
    ),
  ];

  assert(studyIds.length === 1, 'Expected all runs to be within the same study');
  // All runs are from a single study; studySchema is uniform across runs.
  assert(accessibleProcedureRuns.length > 0, 'Expected at least one accessible procedure run');
  const firstStudy = accessibleProcedureRuns[0].study;

  if (isInaccessible(firstStudy)) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={firstStudy}
        modelType={ModelTypeConstants.STUDY}
        displayErrorText={INACCESSIBLE_STUDY_ERROR_TEXT}
      />
    );
  }

  const unitOperationCount = getUnitOperationCountFromProcedureRuns(accessibleProcedureRuns);

  return (
    <ProcedureRunAnalysisDetails
      studyType={firstStudy.studySchema.studyType}
      unitOperationCount={unitOperationCount}
      hasInaccessibleRuns={false}
      ootbDescription={OOTB_STUDY_DESCRIPTION}
    />
  );
}

/**
 * Shared presentational component for the analysis modal UI, used by both
 * ProcedureRunSeedDataConfigEditor and SingleStudyAnalysisDetails (used by StudySeedDataConfigEditor).
 * Callers handle loading and inaccessible states before rendering this.
 */
export function ProcedureRunAnalysisDetails({
  studyType,
  unitOperationCount,
  hasInaccessibleRuns,
  ootbDescription,
}: {
  studyType: StudyType;
  unitOperationCount: number;
  hasInaccessibleRuns: boolean;
  ootbDescription: string;
}): JSX.Element {
  const {classes} = useStyles();
  return (
    <FormGroup>
      <FieldLabel label={SELECTED_UNIT_OPERATIONS_LABEL} />
      <HelpButton
        renderContent={() => (
          <p className={classes.helpToolTipText}>
            Total instances is taken from the number of unit operations, conditions, and replicates.
          </p>
        )}
        iconClassName={classes.helpToolTip}
        interaction={FloatingInteraction.HOVER}
      />
      <Box className={classes.instancesCount}>{unitOperationCount} instances</Box>
      {hasInaccessibleRuns && (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text={getInaccessibleRunsWarningText(studyType)}
        />
      )}
      {getTypedStudyAdapter(studyType).isOOTBChartingEnabled && (
        <div className={classes.ootbCarouselContainer}>
          <OOTBChartingCarousel description={ootbDescription} />
        </div>
      )}
    </FormGroup>
  );
}

export const PROCEDURE_RUNS_ANALYSIS_QUERY: DocumentNode = gql`
  query ProcedureRunsAnalysisQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: STUDIES) {
    procedureRuns(ids: $ids) {
      ... on ProcedureRunNode {
        id
        study {
          ... on StudyNode {
            id
            studySchema {
              id
              studyType
            }
          }
          ...InaccessibleInfoGraphQL
        }
        methodExecutionInstances {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const useStyles = createStyles(() => ({
  instancesCount: {
    marginLeft: '3px',
  },
  helpToolTip: {
    marginLeft: '4px',
  },
  helpToolTipText: {
    marginTop: '5px',
    marginLeft: '25px',
  },
  ootbCarouselContainer: {
    // Stretch the carousel background to the edges of the modal border.
    // * 15px between unit-op count and the carousel. This matches the margin-bottom of FormGroup.
    // * Stretch flush against the sides of the modal body: -16px to offset the padding of the modal body.
    // * Stretch flush against the bottom of the modal body:
    //   (-15px to stretch outside the FormGroup in SeedDataRender)
    //   + (-16px to offset the padding of the modal body)
    margin: '15px -16px -31px -16px',
  },
}));
